import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Trash2, Star, Plus, X, ListChecks, CheckSquare, MoreHorizontal } from 'lucide-react';
import { User } from '../types';
import { ConversationSummary } from '../services/conversationHistoryService';
import { useAuth } from '../hooks/useAuth';
import { useConversationFavorites } from '../hooks/useConversationFavorites';
import UserSettingsModal from './UserSettingsModal';
import ProfilePicture from './ProfilePicture';
import './Sidebar.css';

interface SidebarProps {
  user: User;
  loading?: boolean;
  onUserUpdate?: (updatedUser: Partial<User>) => void;
  onLogout?: () => void;
  onLogoClick?: () => void;
  className?: string;
  // Conversation history props
  conversations?: ConversationSummary[];
  currentConversationId?: string | null;
  onLoadConversation?: (conversationId: string) => void;
  onDeleteConversation?: (conversationId: string) => void;
  onClearAllConversations?: () => void;
  onStartNewConversation?: () => void;
  // Sidebar collapse props
  isCollapsed?: boolean;
  onToggleCollapse?: (collapsed: boolean) => void;
  // User profile for personalization
  userPhotoUrl?: string | null;
  userDisplayName?: string;
}

const Sidebar = React.memo<SidebarProps>(({
  user,
  loading = false,
  onUserUpdate,
  onLogout,
  onLogoClick,
  className = '',
  // Conversation history props
  conversations = [],
  currentConversationId,
  onLoadConversation,
  onDeleteConversation,
  onClearAllConversations,
  onStartNewConversation,
  // Sidebar collapse props
  isCollapsed = false,
  onToggleCollapse,
  // User profile props
  userPhotoUrl,
  userDisplayName
}) => {
  const displayName = userDisplayName || user?.name || 'User';
  const [showSettings, setShowSettings] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [conversationSearchQuery, setConversationSearchQuery] = useState('');

  // Bulk selection state
  const [selectedConversations, setSelectedConversations] = useState<Set<string>>(new Set());
  const [isSelectMode, setIsSelectMode] = useState(false);
  const { hasAdminAccess, hasAnyRole } = useAuth();
  // Temporary release gate: the self-service agent factory (My Agents /
  // Build an Agent) is limited to super admins. Roles mirror the backend
  // getEntraSuperAdminRoles() used by /api/me/agents.
  const isSelfServiceAdmin = hasAnyRole(['super-admin', 'askaldo-admin', 'admin']);
  const { toggleFavorite, isFavorite, sortWithFavoritesFirst } = useConversationFavorites();
  const navigate = useNavigate();
  const menuRef = useRef<HTMLDivElement>(null);

  // Filter conversations based on search query and deduplicate by ID
  const filteredConversations = useMemo(() => {
    // First deduplicate by ID (keep the most recently updated if duplicates exist)
    const uniqueConversations = new Map<string, typeof conversations[0]>();
    
    // Sort by updatedAt descending first so we keep the most recent version of any duplicates
    const sortedByDate = [...conversations].sort((a, b) => 
      new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    );
    
    for (const conv of sortedByDate) {
      if (!uniqueConversations.has(conv.id)) {
        uniqueConversations.set(conv.id, conv);
      }
    }
    
    // Filter by search query
    return Array.from(uniqueConversations.values()).filter(conv => 
      conv.title.toLowerCase().includes(conversationSearchQuery.toLowerCase()) ||
      (conv.lastAgentMessage || conv.lastUserMessage || '').toLowerCase().includes(conversationSearchQuery.toLowerCase())
    );
  }, [conversations, conversationSearchQuery]);

  const sortedConversations = useMemo(() => 
    sortWithFavoritesFirst(filteredConversations),
    [filteredConversations, sortWithFavoritesFirst]
  );

  // Handle menu item clicks
  const handleMenuItemClick = (action: string) => {
    setShowUserMenu(false);
    
    switch (action) {
      case 'settings':
        setShowSettings(true);
        break;
      case 'admin':
        navigate('/admin');
        break;
      case 'patch-notes':
        navigate('/patch-notes');
        break;
      case 'my-agents':
        navigate('/my-agents');
        break;
      case 'build-agent':
        navigate('/build-agent');
        break;
      case 'logout':
        onLogout?.();
        break;
      default:
        break;
    }
  };

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Bulk operations
  const toggleSelectMode = () => {
    setIsSelectMode(!isSelectMode);
    setSelectedConversations(new Set());
  };

  const toggleConversationSelection = (id: string) => {
    const newSelected = new Set(selectedConversations);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedConversations(newSelected);
  };

  const deleteSelectedConversations = () => {
    selectedConversations.forEach(id => {
      onDeleteConversation?.(id);
    });
    setSelectedConversations(new Set());
    setIsSelectMode(false);
  };

  const selectAllConversations = () => {
    if (selectedConversations.size === sortedConversations.length) {
      setSelectedConversations(new Set());
    } else {
      setSelectedConversations(new Set(sortedConversations.map(conv => conv.id)));
    }
  };

  // Helper function to format dates
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return 'Today';
    if (diffDays === 2) return 'Yesterday';
    if (diffDays <= 7) return `${diffDays - 1} days ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className={`bg-gradient-to-b from-scout-rail to-scout-rail-deep dark:from-scout-rail-dark dark:to-scout-rail-deep-dark dark:shadow-[inset_-1px_0_0_rgba(255,255,255,0.07)] text-scout-rail-ink h-full flex flex-col transition-all duration-300 ${
      isCollapsed ? 'w-16' : 'w-64'
    } ${className}`} style={{ gridArea: 'sidebar' }}>
      {isCollapsed ? (
        /* Collapsed state - show minimal content */
        <div className="flex flex-col items-center justify-start pt-4 h-full">
          {/* Show UniFirst logo when collapsed */}
          <div className="mb-4 cursor-pointer" onClick={onLogoClick}>
            <img
              src="/assets/logos/smallu.png"
              alt="UniFirst Logo"
              className="w-10 h-10 object-contain brightness-0 invert"
            />
          </div>
        </div>
      ) : (
        /* Expanded state - show all content */
        <>
          {/* Fixed header sections */}
          <div className="flex-shrink-0 px-4 pt-5 pb-3 space-y-4">
            {/* The wordmark is a transparent PNG, so a filter renders it white on the rail without a new
                asset. Rendered directly rather than through <Logo>, whose sidebar styles reset the filter. */}
            <button type="button" onClick={onLogoClick} className="flex w-full justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60 rounded" aria-label="Scout Central home">
              <img
                src="/assets/logos/unifirst-sidebar-logo.png"
                alt="UniFirst"
                className="h-8 w-auto brightness-0 invert"
                draggable={false}
              />
            </button>

            <button
              onClick={() => onStartNewConversation?.()}
              className="w-full flex items-center justify-center gap-2 px-3 py-2.5 text-sm font-semibold text-white bg-brand-500 hover:bg-brand-600 rounded-lg shadow-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
            >
              <Plus className="h-4 w-4" aria-hidden />
              New conversation
            </button>

            {/* Search */}
            <div className="relative">
              <input
                type="text"
                placeholder="Search conversations…"
                aria-label="Search conversations"
                value={conversationSearchQuery}
                onChange={(e) => setConversationSearchQuery(e.target.value)}
                className="w-full px-3 py-2 pl-9 text-sm text-scout-rail-ink placeholder:text-scout-rail-muted bg-white/5 border border-scout-rail-line rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent"
              />
              <div className="absolute left-3 top-2.5 text-scout-rail-muted">
                <Search size={16} />
              </div>
            </div>
          </div>

          {/* Main content area with flexible sections */}
          <div className="flex-1 flex flex-col min-h-0">
            <div className="flex-1 flex flex-col min-h-0">
              {/* Section label with bulk operations */}
              <div className="flex-shrink-0 px-4 pt-2 pb-1">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase tracking-wider text-scout-rail-muted">
                    Recent
                  </span>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={toggleSelectMode}
                      className="p-1 text-scout-rail-muted hover:text-white rounded-md transition-colors"
                      title={isSelectMode ? "Exit select mode" : "Select conversations"}
                    >
                      {isSelectMode ? <X className="h-3.5 w-3.5" /> : <ListChecks className="h-3.5 w-3.5" />}
                    </button>
                    {isSelectMode && (
                      <>
                        <button
                          onClick={selectAllConversations}
                          className="p-1 text-scout-rail-muted hover:text-white rounded-md transition-colors"
                          title="Select all"
                        >
                          <CheckSquare className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={deleteSelectedConversations}
                          className="p-1 text-red-300 hover:text-red-200 rounded-md transition-colors disabled:opacity-40"
                          title="Delete selected"
                          disabled={selectedConversations.size === 0}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Scrollable conversations list */}
              <div className="flex-1 overflow-y-auto scrollbar-hidden">
                <div className="space-y-0.5 px-2 pb-2">
                  {sortedConversations.length === 0 && (
                    <p className="px-3 py-4 text-xs text-scout-rail-muted">
                      {conversationSearchQuery
                        ? 'No conversations match that search.'
                        : 'Your conversations will appear here.'}
                    </p>
                  )}
                  {sortedConversations.map((conversation) => (
                    <div
                      key={conversation.id}
                      className={`group relative px-3 py-2.5 rounded-lg cursor-pointer transition-colors ${
                        currentConversationId === conversation.id
                          ? 'bg-scout-rail-active'
                          : 'hover:bg-scout-rail-hover'
                      }`}
                      onClick={() => {
                        if (isSelectMode) {
                          toggleConversationSelection(conversation.id);
                        } else {
                          onLoadConversation?.(conversation.id);
                        }
                      }}
                    >
                      <div className="flex items-start gap-3">
                        {isSelectMode && (
                          <input
                            type="checkbox"
                            checked={selectedConversations.has(conversation.id)}
                            onChange={() => toggleConversationSelection(conversation.id)}
                            className="mt-1 text-brand-500 rounded"
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline justify-between gap-2 mb-0.5">
                            <h4 className="text-sm font-medium text-white truncate">
                              {conversation.title}
                            </h4>
                            <span className="text-[11px] text-scout-rail-muted flex-shrink-0 tabular-nums">
                              {formatDate(conversation.updatedAt)}
                            </span>
                          </div>
                          <p className="text-xs text-scout-rail-muted line-clamp-1">
                            {conversation.lastAgentMessage || conversation.lastUserMessage || 'No messages yet'}
                          </p>
                        </div>
                        {!isSelectMode && (
                          <div className="flex items-center gap-0.5">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleFavorite(conversation.id);
                              }}
                              className={`p-1 transition-all ${
                                isFavorite(conversation.id)
                                  ? 'text-amber-300 opacity-100'
                                  : 'opacity-0 group-hover:opacity-100 text-scout-rail-muted hover:text-amber-300'
                              }`}
                              title={isFavorite(conversation.id) ? 'Remove bookmark' : 'Bookmark conversation'}
                            >
                              <Star className="h-3.5 w-3.5" fill={isFavorite(conversation.id) ? 'currentColor' : 'none'} />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onDeleteConversation?.(conversation.id);
                              }}
                              className="opacity-0 group-hover:opacity-100 p-1 text-scout-rail-muted hover:text-red-300 transition-all"
                              title="Delete conversation"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* User Profile - Fixed at bottom, visually docked */}
          <div className="flex-shrink-0 px-4 py-3.5 border-t border-scout-rail-line">
          <div className="flex items-center gap-3">
            <ProfilePicture
              photoUrl={userPhotoUrl}
              displayName={displayName}
              size="medium"
              showBorder={false}
            />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">
                {displayName}
                </p>
                <p className="text-xs text-scout-rail-muted truncate">
                  {user.id}
                </p>
              </div>
              <div className="relative" ref={menuRef}>
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="p-1.5 rounded-md text-scout-rail-muted hover:text-white hover:bg-scout-rail-hover transition-colors"
                  aria-label="Account menu"
                >
                  <MoreHorizontal className="h-4 w-4" />
                </button>
                {showUserMenu && (
                  <div className="absolute bottom-full right-0 mb-2 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50">
                    <div className="py-1">
                      <button
                        onClick={() => handleMenuItemClick('settings')}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        Settings
                      </button>
                      {/* AA-207 self-service entries — temporarily limited to
                          super admins (see isSelfServiceAdmin). */}
                      {isSelfServiceAdmin && (
                        <>
                          <button
                            onClick={() => handleMenuItemClick('my-agents')}
                            className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                          >
                            My Agents
                          </button>
                          <button
                            onClick={() => handleMenuItemClick('build-agent')}
                            className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                          >
                            Build an Agent
                          </button>
                        </>
                      )}
                      {hasAdminAccess() && (
                        <button
                          onClick={() => handleMenuItemClick('admin')}
                          className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                        >
                          Admin Panel
                        </button>
                      )}
                      {/* Patch Notes menu item temporarily disabled
                      <button
                        onClick={() => handleMenuItemClick('patch-notes')}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        Patch Notes
                      </button>
                      */}
                      <button
                        onClick={() => handleMenuItemClick('logout')}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        Logout
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* User Settings Modal */}
      {showSettings && (
        <UserSettingsModal
          isOpen={showSettings}
          user={user}
          onClose={() => setShowSettings(false)}
          onUserUpdate={onUserUpdate || (() => {})}
        />
      )}
    </div>
  );
});

export default Sidebar;